import { gql } from '@apollo/client';

// User Queries
export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      email
      name
      phone
      avatarURL
      onboardingCompleted
      streamingSetupCompleted
      createdAt
      updatedAt
      lastLoginAt
      ssoVerified
      authMethods {
        id
        provider
        hasPassword
        createdAt
      }
      organizations {
        id
        name
        role
        isPersonal
      }
    }
  }
`;

export const GET_CURRENT_USER_TUTORIAL_PROGRESS = gql`
  query GetCurrentUserTutorialProgress {
    me {
      id
      onboardingCompleted
      tutorialProgress {
        dashboard {
          version
          status
          updatedAt
        }
        livestreamModerator {
          version
          status
          updatedAt
        }
      }
    }
  }
`;

export const GET_USER_ORGANIZATIONS = gql`
  query GetUserOrganizations {
    me {
      organizations {
        id
        name
        role
        isPersonal
      }
    }
  }
`;

export const GET_MY_REFERRAL_PROGRAM = gql`
  query GetMyReferralProgram {
    myReferralProgram {
      code
      shareURL
      pendingReferrals
      completedReferrals
      earnedCredits
    }
  }
`;

// Organization Queries
export const GET_ORGANIZATION = gql`
  query GetOrganization($id: ID!) {
    organization(id: $id) {
      id
      name
      welcomeCreditsGranted
      settings {
        max_empty_timeout
        no_moderator_timeout
        billing_contact_name
        billing_contact_email
        branding {
          logo_url
          brand_name
          accent_color
          redirect_url
          pre_join_bg_color
          welcome_message
        }
      }
    }
  }
`;

export const GET_ORGANIZATION_MEMBERS_CONNECTION = gql`
  query GetOrganizationMembersConnection($organizationId: ID!, $first: Int, $after: String) {
    organizationMembersConnection(organizationId: $organizationId, first: $first, after: $after) {
      nodes {
        id
        name
        email
        role
        avatarUrl
        invitationId
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// Session Queries
export const GET_SESSIONS = gql`
  query GetSessions($limit: Int, $offset: Int) {
    sessions(limit: $limit, offset: $offset) {
      id
      date
      duration
      participants
      creditsUsed
      status
      createdAt
      flowRoomName
      title
      description
      notes
    }
  }
`;

export const GET_SESSION = gql`
  query GetSession($id: ID!) {
    session(id: $id) {
      id
      date
      duration
      participants
      creditsUsed
      status
      createdAt
      flowRoomName
      title
      description
      notes
      createdBy {
        id
        name
        email
      }
      sessionParticipants {
        id
        participantName
        participantIdentity
        isModerator
        joinedAt
        leftAt
      }
      streamingPeriods {
        id
        ingressId
        startedAt
        endedAt
      }
    }
  }
`;

export const GET_SESSION_FEEDBACK = gql`
  query GetSessionFeedback($sessionId: ID!) {
    sessionFeedback(sessionId: $sessionId) {
      id
      sessionId
      userId
      participantIdentity
      isModerator
      overallRating
      comments
      selectedIssues
      createdAt
      updatedAt
    }
  }
`;

export const GET_SESSION_ISSUES = gql`
  query GetSessionIssues($sessionId: ID!) {
    sessionIssues(sessionId: $sessionId) {
      id
      sessionId
      userId
      participantIdentity
      issueDescription
      createdAt
      updatedAt
    }
  }
`;

export const GET_RECENT_SESSIONS = gql`
  query GetRecentSessions($limit: Int = 5) {
    sessions(limit: $limit) {
      id
      date
      duration
      participants
      creditsUsed
      status
      title
      description
      notes
    }
  }
`;

export const GET_ACTIVE_SESSIONS = gql`
  query GetActiveSessions {
    activeSessions {
      id
      date
      duration
      participants
      creditsUsed
      status
      title
      description
      flowRoomName
      createdBy {
        id
        name
        email
      }
    }
  }
`;

// Credits Queries
export const GET_CREDITS = gql`
  query GetCredits {
    credits {
      balance
      subscriptionCredits
      prepaidCredits
      billableCredits
      billableAmountUsd
      overageLockActive
      monthlyAllocation
      usedThisMonth
      totalUsed
    }
  }
`;

export const GET_PERSONAL_ROOMS = gql`
  query GetPersonalRooms {
    personalRooms {
      id
      roomName
      title
      description
      sessionCount
      createdAt
      createdBy {
        id
        name
      }
    }
  }
`;

export const CHECK_ROOM_NAME_AVAILABILITY = gql`
  query CheckRoomNameAvailability($roomName: String!) {
    checkRoomNameAvailability(roomName: $roomName)
  }
`;

export const GET_CREDIT_USAGE = gql`
  query GetCreditUsage($startDate: String, $endDate: String) {
    creditUsage(startDate: $startDate, endDate: $endDate) {
      month
      creditsUsed
      amountSpent
    }
  }
`;

export const GET_CREDIT_TRANSACTIONS_CONNECTION = gql`
  query GetCreditTransactionsConnection($startDate: String, $endDate: String, $first: Int, $after: String) {
    creditTransactionsConnection(startDate: $startDate, endDate: $endDate, first: $first, after: $after) {
      nodes {
        id
        sessionId
        transactionType
        amount
        description
        creditSource
        createdAt
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_BILLING_INVOICES_CONNECTION = gql`
  query GetBillingInvoicesConnection($first: Int, $after: String) {
    billingInvoicesConnection(first: $first, after: $after) {
      nodes {
        id
        status
        billingReason
        amountDueUsd
        amountPaidUsd
        currency
        hostedInvoiceUrl
        invoicePdfUrl
        paymentMethodBrand
        paymentMethodLast4
        invoiceCreatedAt
        finalizedAt
        paidAt
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// Subscription Queries
export const GET_SUBSCRIPTION = gql`
  query GetSubscription {
    subscription {
      id
      plan
      status
      billingProvider
      pendingBillingProvider
      billingProviderSwitchAt
      monthlyCredits
      rolloverCredits
      personalRooms
      billingCycleStart
      billingCycleEnd
      priceUsd
      createdAt
      cancelledAt
      expiresAt
      scheduledChange {
        id
        sourcePlan
        targetPlan
        effectiveOn
        status
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_SUBSCRIPTION_PLAN = gql`
  query GetSubscriptionPlan {
    subscription {
      id
      plan
      status
    }
  }
`;

export const GET_BILLING_CAPABILITIES = gql`
  query GetBillingCapabilities {
    billingCapabilities {
      subscriptionCheckoutEnabled
      subscriptionCheckoutUnavailableReason
      automaticBillingSetupEnabled
      automaticBillingSetupUnavailableReason
      automaticBillingReady
      prepaidTopupEnabled
      prepaidTopupUnavailableReason
      billingPortalEnabled
      billingPortalUnavailableReason
    }
  }
`;

export const GET_PLAN_CONFIGS = gql`
  query GetPlanConfigs {
    planConfigs {
      plan
      baseCredits
      creditStep
      pricePerStep
      basePrice
      basePersonalRooms
      maxPersonalRooms
      pricePerRoom
      maxTeamMembers
      hasConnectors
    }
  }
`;

export const GET_CREDITS_WITH_BREAKDOWN = gql`
  query GetCreditsWithBreakdown {
    credits {
      balance
      subscriptionCredits
      prepaidCredits
      billableCredits
      billableAmountUsd
      overageLockActive
      monthlyAllocation
      billingCycleEnd
      usedThisMonth
      totalUsed
    }
  }
`;

// Dashboard Stats Queries
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($month: String, $year: Int) {
    dashboardStats(month: $month, year: $year) {
      totalSessions
      totalHours
      avgCreditsPerSession
      creditsUsed
      recentSessions {
        id
        date
        duration
        creditsUsed
      }
    }
  }
`;

// User Role Query
export const GET_USER_ROLE = gql`
  query GetUserRole($organizationId: ID!) {
    userRole(organizationId: $organizationId) {
      role
      isAdmin
    }
  }
`;

// Invitation Queries
export const VALIDATE_INVITATION = gql`
  query ValidateInvitation($token: String!) {
    validateInvitation(token: $token) {
      valid
      email
      organizationName
      inviterName
      role
      expiresAt
      error
    }
  }
`;

// Plan Change Preview
export const PREVIEW_PLAN_CHANGE = gql`
  query PreviewPlanChange($targetPlan: SubscriptionPlan!) {
    previewPlanChange(targetPlan: $targetPlan) {
      currentPlan
      targetPlan
      creditsRemaining
      rolloverCredits
      usedThisCycle
      targetMonthlyCredits
      totalAfterUpgrade
      prepaidCredits
    }
  }
`;

export const STREAMING_SETUP_STATUS = gql`
  query StreamingSetupStatus($ingressId: String!) {
    streamingSetupStatus(ingressId: $ingressId) {
      ingressId
      roomName
      path
      url
      streamKey
      state {
        status
        error
      }
      video {
        mimeType
        codec
        averageBitrate
        width
        height
        framerate
      }
      audio {
        mimeType
        codec
        averageBitrate
        channels
        sampleRate
      }
      hasVideo
      hasAudio
      verified
      createdAt
      verifiedAt
    }
  }
`;
